import React, { useMemo, useState, useEffect } from "react";
import PublicHeader from "../components/PublicHeader";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/ProjectPage.css";
import "../styles/CalendarPage.css";
import { EventService } from "../services/EventService";
import { useAuth } from "../context/AuthContext";
import SiteMenu from "../components/SiteMenu";
import { Event } from "../types/Event";

const EventDateBadge: React.FC<{ startDate: string; endDate?: string }> = ({ startDate, endDate }) => {
  const now = new Date();
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : null;
  const isUpcoming = now < start;

  return (
    <span className={`event-date-badge ${isUpcoming ? "upcoming" : "past"}`}>
      {start.toLocaleDateString("fr-FR")}
      {end ? ` - ${end.toLocaleDateString("fr-FR")}` : ""}
    </span>
  );
};

const CalendarPage: React.FC = () => {
  const { api, user } = useAuth();

  const activities = [
    { label: "Appel d'offre", path: "offre" },
    { label: "Projet", path: "/project" },
    { label: "Calendrier", path: "/calendar" },
    { label: "Événements", path: "/event" },
  ];

  const [events, setEvents] = useState<Event[]>([]);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [showModal, setShowModal] = useState(false);
  const [newEvent, setNewEvent] = useState<Partial<Event>>({
    titre: "",
    description: "",
    lieu: "",
    start_date: "",
    end_date: "",
    images: [],
  });

  // Nouvel état pour gérer l'ouverture/fermeture des descriptions
  const [expandedDescriptions, setExpandedDescriptions] = useState<{ [key: number]: boolean }>({});

  const toggleDescription = (id: number) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await EventService.getAllEvents(api);
        setEvents(data);
      } catch (error) {
        console.error("Erreur lors de la récupération des événements :", error);
      }
    };
    fetchEvents();
  }, [api]);

  const filtered = useMemo(() => {
    if (!query) return events;
    const q = query.toLowerCase();
    return events.filter(
      (e) =>
        e.titre.toLowerCase().includes(q) ||
        (e.lieu && e.lieu.toLowerCase().includes(q)) ||
        (e.description && e.description.toLowerCase().includes(q))
    );
  }, [events, query]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageData = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleAddEvent = () => {
    setNewEvent({ titre: "", description: "", lieu: "", start_date: "", end_date: "", images: [] });
    setShowModal(true);
  };

  const handleEditEvent = (event: Event) => {
    setNewEvent(event);
    setShowModal(true);
  };

  const handleSaveEvent = async () => {
    if (!newEvent.titre || !newEvent.start_date) {
      alert("Veuillez renseigner au moins le titre et la date de début.");
      return;
    }

    let savedEvent: Event;

    if (newEvent.id_event) {
      savedEvent = await EventService.updateEvent(api, newEvent as Event);
      setEvents(events.map((e) => (e.id_event === newEvent.id_event ? (newEvent as Event) : e)));
    } else {
      const nextId = Math.max(...events.map((e) => e.id_event), 0) + 1;
      savedEvent = await EventService.createEvent(api, newEvent as Event);
      setEvents([...events, { ...newEvent, id_event: nextId } as Event]);
    }
    setShowModal(false);
  };

  return (
    <div className="project-page-wrapper">
      <div className="map-page-header mb-3">
        <PublicHeader />
      </div>

      <div className="container-fluid">
        <div className="row">
          <div className="col-12 col-md-3 col-lg-2 mb-3 mb-md-0">
            <SiteMenu activities={activities} />
          </div>

          <main className="col-12 col-md-9 col-lg-10 project-page">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
              <h2>Liste des événements</h2>
              {user && (user.roleRank??0)>=5 && (
                <button className="btn-add-event" onClick={handleAddEvent}>
                  <i className="bi bi-plus-lg"></i> Ajouter un événement
                </button>
              )}
            </div>

            {/* Barre de recherche */}
            <div className="filters-bar mb-3">
              <div className="input-wrapper">
                <span className="search-icon">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Rechercher titre / lieu / description"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Tableau */}
            {events.length === 0 ? (
              <p className="text-muted">Aucun événement trouvé.</p>
            ) : (
              <div className="table-responsive shadow-sm">
                <table className="table table-hover align-middle">
                  <thead className="table-success">
                    <tr>
                      <th>Titre</th>
                      <th>Description</th>
                      <th>Lieu</th>
                      <th>Dates</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageData.map((event) => (
                      <tr key={event.id_event}>
                        <td>{event.titre}</td>
                        <td
                          onClick={() => toggleDescription(event.id_event!)}
                          style={{ cursor: "pointer" }}
                        >
                        {expandedDescriptions[event.id_event!]
                          ? event.description || ""
                          : (event.description?.length || 0) > 50
                            ? event.description!.substring(0, 50) + "..."
                            : event.description || ""}
                        </td>
                        <td>{event.lieu}</td>
                        <td>
                          <EventDateBadge startDate={event.start_date} endDate={event.end_date} />
                        </td>
                        
                        <td className="text-center">
                          {user && (user.roleRank??0)>=5 && (
                            <button
                              className="btn btn-sm btn-outline-success"
                              onClick={() => handleEditEvent(event)}
                            >
                              <i className="bi bi-pencil-square"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
              <small className="text-muted">
                {pageData.length} / {total}
              </small>
              <nav>
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => setPage(page - 1)}>
                      <i className="bi bi-chevron-left"></i>
                    </button>
                  </li>
                  {pageNumbers.map((num) => (
                    <li key={num} className={`page-item ${num === currentPage ? "active" : ""}`}>
                      <button className="page-link" onClick={() => setPage(num)}>
                        {num}
                      </button>
                    </li>
                  ))}
                  <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => setPage(page + 1)}>
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>

            {/* Modal */}
            {showModal && (
              <div className="modal-overlay" onClick={() => setShowModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <h4>{newEvent.id_event ? "Modifier l'événement" : "Ajouter un nouvel événement"}</h4>

                  <div className="form-group">
                    <label>Titre</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newEvent.titre}
                      onChange={(e) => setNewEvent({ ...newEvent, titre: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      className="form-control"
                      value={newEvent.description}
                      onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Lieu</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newEvent.lieu}
                      onChange={(e) => setNewEvent({ ...newEvent, lieu: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Date début</label>
                    <input
                      type="date"
                      className="form-control"
                      value={newEvent.start_date}
                      onChange={(e) => setNewEvent({ ...newEvent, start_date: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Date fin</label>
                    <input
                      type="date"
                      className="form-control"
                      value={newEvent.end_date}
                      onChange={(e) => setNewEvent({ ...newEvent, end_date: e.target.value })}
                    />
                  </div>

                  <div className="mt-3 d-flex justify-content-end gap-2">
                    <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                      Annuler
                    </button>
                    <button className="btn-add-event" onClick={handleSaveEvent}>
                      Enregistrer
                    </button>
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default CalendarPage;
